import React, { useEffect, useRef, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { toast } from "sonner";
import api, { fileUrl, formatPrice } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LanguageContext";
import { useSettings } from "@/context/SettingsContext";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import EmiCalculator from "@/components/EmiCalculator";
import ShareMenu from "@/components/ShareMenu";
import WhatsAppContact from "@/components/WhatsAppContact";
import MobileStickyCTA from "@/components/MobileStickyCTA";
import SidebarLoanOptions from "@/components/SidebarLoanOptions";
import { StarRating, StarInput } from "@/components/StarRating";
import {
  MapPin, BedDouble, Bath, Maximize, Ruler, BadgeCheck, FileText, Loader2, UserRound,
  Building2, Shield, Landmark, HeartPulse, Pill, School, GraduationCap,
  TrainFront, Bus, Clapperboard, Trees, QrCode, Phone, X, ChevronLeft, ChevronRight, CalendarClock, CheckCircle2, Compass, BadgePercent, Ticket,
  Eye, Flag, PlayCircle, TrendingUp, Navigation, Bike, Star, Share2, Copy,
} from "lucide-react";

const nearbyMeta = {
  sub_register_office: { icon: Landmark, label: "Sub-Register Office" },
  police_station: { icon: Shield, label: "Police Station" },
  hospital: { icon: HeartPulse, label: "Hospital" },
  pharmacy: { icon: Pill, label: "Pharmacy" },
  school: { icon: School, label: "School" },
  college: { icon: GraduationCap, label: "College / University" },
  railway_station: { icon: TrainFront, label: "Railway Station" },
  bus_station: { icon: Bus, label: "Bus Stand" },
  cinema: { icon: Clapperboard, label: "Entertainment" },
  park: { icon: Trees, label: "Park" },
};

export default function PropertyDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useLang();
  const { feature } = useSettings();
  const [prop, setProp] = useState(null);
  const [active, setActive] = useState(0);
  const [nearby, setNearby] = useState(null);
  const [nearbyLoading, setNearbyLoading] = useState(true);
  const [enquiries, setEnquiries] = useState([]);
  const [settings, setSettings] = useState(null);
  const [enqOpen, setEnqOpen] = useState(false);
  const [enqMsg, setEnqMsg] = useState("");
  const [enqPhone, setEnqPhone] = useState("");
  const [enqDial, setEnqDial] = useState("+91");
  const [enqInterest, setEnqInterest] = useState("");
  const [checkoutOffers, setCheckoutOffers] = useState([]);
  const [couponCodes, setCouponCodes] = useState({});
  const [lightbox, setLightbox] = useState(-1);
  const [submitting, setSubmitting] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);
  const [reportReason, setReportReason] = useState("");
  const [rating, setRating] = useState({ avg: 0, count: 0, my_rating: null, reviews: [] });
  const [myStars, setMyStars] = useState(0);
  const [myReview, setMyReview] = useState("");
  const hydratedRef = useRef(false);
  const ratedBrokerRef = useRef(null);

  const loadRating = async (brokerId, force = false) => {
    if (!brokerId) return;
    if (!force && ratedBrokerRef.current === brokerId) return;  // avoid duplicate fetches
    const { data } = await api.get(`/brokers/${brokerId}/rating`);
    ratedBrokerRef.current = brokerId;  // mark only after a successful response
    setRating(data);
    if (!hydratedRef.current) {  // hydrate the input only once; never clobber in-progress input
      if (data.my_rating) { setMyStars(data.my_rating.stars); setMyReview(data.my_rating.review || ""); }
      hydratedRef.current = true;
    }
  };

  const submitRating = async () => {
    if (!myStars) { toast.error("Please select a star rating"); return; }
    try {
      await api.post(`/brokers/${prop.broker.id}/rate`, { stars: myStars, review: myReview });
      toast.success("Thanks for rating!");
      loadRating(prop.broker.id, true);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not submit rating"); }
  };

  const mapsLink = () => {
    const q = prop.lat != null && prop.lon != null ? `${prop.lat},${prop.lon}` : encodeURIComponent([prop.address, prop.area_name, prop.city, prop.pincode].filter(Boolean).join(", "));
    return `https://www.google.com/maps/search/?api=1&query=${q}`;
  };
  const rapidoLink = () => {
    const q = prop.lat != null && prop.lon != null ? `${prop.lat},${prop.lon}` : encodeURIComponent([prop.area_name, prop.city, prop.pincode].filter(Boolean).join(", "));
    return `https://www.google.com/maps/dir/?api=1&destination=${q}&travelmode=driving`;
  };
  const fullAddress = () => {
    const raw = [prop.address, prop.area_name, prop.city].map((s) => (s || "").trim()).filter(Boolean);
    const parts = [];
    raw.forEach((s) => { if (!parts.some((p) => p.toLowerCase().includes(s.toLowerCase()))) parts.push(s); });
    let out = parts.join(", ");
    if (prop.pincode) out += ` - ${prop.pincode}`;
    return out;
  };

  const submitReport = async () => {
    if (!user) { toast.info("Please login to report a listing"); return; }
    try {
      await api.post("/reports", { property_id: id, reason: reportReason });
      toast.success("Thanks — our team will review this listing.");
      setReportOpen(false); setReportReason("");
    } catch (e) { toast.error("Could not submit report"); }
  };

  const embedUrl = (url) => {
    if (!url) return "";
    const yt = url.match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/|shorts\/))([\w-]{11})/);
    if (yt) return `https://www.youtube.com/embed/${yt[1]}`;
    return url;
  };

  const loadEnquiries = async () => {
    if (user && user.role === "customer") {
      const { data } = await api.get("/enquiries");
      setEnquiries(data.enquiries.filter((e) => e.property_id === id));
    }
  };

  useEffect(() => {
    if (user === null) return; // wait until auth resolves; guests may view too
    api.get(`/properties/${id}`).then((r) => { setProp(r.data); if (r.data.city) localStorage.setItem("mp_area", r.data.city); if (r.data.broker?.id) loadRating(r.data.broker.id); }).catch(() => toast.error("Not found"));
    api.get(`/properties/${id}/nearby`).then((r) => setNearby(r.data.nearby)).catch(() => setNearby(null)).finally(() => setNearbyLoading(false));
    api.get("/settings").then((r) => setSettings(r.data));
    loadEnquiries();
    // eslint-disable-next-line
  }, [id, user]);

  // Real-time auto-refresh of property + enquiries
  useEffect(() => {
    const iv = setInterval(() => {
      api.get(`/properties/${id}`, { params: { count: 0 } }).then((r) => setProp(r.data)).catch(() => {});
      loadEnquiries();
    }, 12000);
    return () => clearInterval(iv);
    // eslint-disable-next-line
  }, [id, user]);

  const myEnquiry = enquiries[0];
  const confirmed = myEnquiry && myEnquiry.status === "confirmed";

  useEffect(() => {
    const conf = enquiries.some((e) => e.status === "confirmed");
    if (conf && prop && prop.city) {
      api.get("/offers/active", { params: { city: prop.city, pincode: prop.pincode } }).then((r) => setCheckoutOffers(r.data.offers)).catch(() => {});
    }
  }, [enquiries, prop]);

  const validateCoupon = async (offerId) => {
    try {
      const { data } = await api.post(`/offers/${offerId}/validate`);
      setCouponCodes((v) => ({ ...v, [offerId]: data.coupon_code || "APPLIED" }));
      toast.success(`Coupon applied: ${data.coupon_code || ""}`);
    } catch (e) {
      toast.error("Could not apply coupon");
    }
  };

  const submitEnquiry = async () => {
    setSubmitting(true);
    try {
      await api.post("/enquiries", { property_id: id, message: enqMsg, customer_phone: enqPhone.trim() ? `${enqDial}${enqPhone.replace(/[^0-9]/g, "")}` : "", customer_name: user.name });
      toast.success(t("enquiry.sent"));
      setEnqOpen(false);
      setEnqMsg("");
      loadEnquiries();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    } finally { setSubmitting(false); }
  };

  const copyId = async () => {
    const link = `${window.location.origin}/property/${prop.id}`;
    try {
      await navigator.clipboard.writeText(`Property ID: ${prop.short_code}\n${link}`);
      toast.success("Property ID & link copied");
    } catch (e) { toast.error("Could not copy — please copy manually"); }
  };

  const openEnquiry = () => {
    if (!user) { navigate(`/login?redirect=${encodeURIComponent(location.pathname)}`); return; }
    setEnqOpen(true);
  };

  if (user === null) return <div className="flex justify-center py-32" data-testid="detail-auth-loading"><Loader2 className="w-8 h-8 animate-spin text-brand-emerald" /></div>;
  if (!prop) return <div className="flex justify-center py-32"><Loader2 className="w-8 h-8 animate-spin text-brand-emerald" /></div>;

  const photos = (prop.photos || []).map(fileUrl);
  const typeLabel = { sale: t("prop.sale"), rent: t("prop.rent"), lease: t("prop.lease") }[prop.listing_type];

  return (
    <div className="max-w-6xl mx-auto px-5 sm:px-8 py-8 pb-24 lg:pb-8" data-testid="property-detail">
      <div className="flex flex-col lg:grid lg:grid-cols-[1.6fr_1fr] gap-8">
        {/* Gallery */}
        <div className="order-1 lg:order-none lg:col-start-1 lg:row-start-1">
          <div className="rounded-2xl overflow-hidden bg-secondary aspect-[16/10] border border-brand-line relative">
            {photos.length ? <img src={photos[active]} alt={prop.title} onClick={() => setLightbox(active)} className="w-full h-full object-cover cursor-zoom-in" data-testid="gallery-main" /> : <div className="w-full h-full flex items-center justify-center text-brand-muted">No image</div>}
            {prop.status === "sold" && <div className="absolute top-4 left-4 bg-brand-terracotta text-white font-bold px-4 py-1.5 rounded-lg">{t("prop.sold")}</div>}
            {photos.length > 0 && <span className="absolute bottom-3 right-3 bg-black/50 text-white text-xs px-2.5 py-1 rounded-full">{active + 1} / {photos.length} · tap to zoom</span>}
          </div>
          {photos.length > 1 && (
            <div className="flex gap-3 mt-3 overflow-x-auto no-scrollbar">
              {photos.map((p, i) => (
                <button key={i} onClick={() => setActive(i)} className={`w-24 h-20 rounded-xl overflow-hidden shrink-0 border-2 ${active === i ? "border-brand-emerald" : "border-transparent"}`}>
                  <img src={p} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Details column */}
        <div className="order-3 lg:order-none lg:col-start-1 lg:row-start-2">
          {/* Details */}
          <div className="mt-8">
            <h2 className="font-heading font-bold text-2xl text-brand-ink tracking-tight mb-4">{t("prop.details")}</h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
              {prop.bedrooms != null && <DetailStat icon={BedDouble} label={t("prop.beds")} value={prop.bedrooms} />}
              {prop.bathrooms != null && <DetailStat icon={Bath} label={t("prop.baths")} value={prop.bathrooms} />}
              {prop.area_sqft != null && <DetailStat icon={Maximize} label={t("prop.area")} value={`${prop.area_sqft} sqft`} />}
              {prop.dimensions && <DetailStat icon={Ruler} label={t("prop.dimensions")} value={prop.dimensions} />}
              <DetailStat icon={Building2} label={t("filter.type")} value={prop.property_type} />
              {prop.rera_license && <DetailStat icon={BadgeCheck} label={t("prop.rera")} value={prop.rera_license} />}
              {prop.facing && <DetailStat icon={Compass} label={t("form.facing")} value={prop.facing} />}
            </div>
            {prop.description && <p className="text-brand-ink/80 leading-relaxed mt-6">{prop.description}</p>}
            {(prop.amenities || []).length > 0 && (
              <div className="mt-6" data-testid="prop-amenities">
                <div className="text-sm font-semibold text-brand-ink mb-2">Facilities & Amenities</div>
                <div className="flex flex-wrap gap-2">
                  {prop.amenities.map((a) => (
                    <span key={a} className="inline-flex items-center gap-1.5 text-sm rounded-full bg-brand-emerald/10 text-brand-emerald px-3 py-1 font-medium">✓ {a}</span>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Detailed dimensions */}
          {prop.detailed_dimensions && prop.detailed_dimensions.length > 0 && (
            <div className="mt-8">
              <h3 className="font-heading font-semibold text-lg mb-3 flex items-center gap-2"><Ruler className="w-5 h-5 text-brand-emerald" /> {t("dim.title")}</h3>
              <div className="grid sm:grid-cols-2 gap-3">
                {prop.detailed_dimensions.map((d, i) => (
                  <div key={i} className="flex justify-between bg-card border border-brand-line rounded-xl px-4 py-3" data-testid={`detail-dim-${i}`}>
                    <span className="text-brand-ink font-medium">{d.label || "Dimension"}</span>
                    <span className="text-brand-emerald font-heading font-semibold">{d.value} {d.unit}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Documents */}
          {prop.documents && prop.documents.length > 0 && (
            <div className="mt-8">
              <h3 className="font-heading font-semibold text-lg mb-3">{t("prop.documents")}</h3>
              <div className="flex flex-wrap gap-3">
                {prop.documents.map((d, i) => (
                  <a key={i} href={fileUrl(d.url || d.path)} target="_blank" rel="noreferrer" className="flex items-center gap-2 px-4 py-2 border border-brand-line rounded-xl text-sm hover:border-brand-emerald">
                    <FileText className="w-4 h-4 text-brand-emerald" /> {d.name || `Document ${i + 1}`}
                  </a>
                ))}
              </div>
            </div>
          )}

          {/* Location / Address */}
          <div className="mt-8" data-testid="location-block">
            <h3 className="font-heading font-semibold text-lg mb-3 flex items-center gap-2"><MapPin className="w-5 h-5 text-brand-emerald" /> {t("prop.location")}</h3>
            <div className="bg-card border border-brand-line rounded-2xl p-5">
              <p className="text-brand-ink font-medium" data-testid="full-address">{fullAddress()}</p>
              <div className="flex flex-wrap gap-3 mt-4">
                <a href={mapsLink()} target="_blank" rel="noopener noreferrer" data-testid="open-google-maps" className="inline-flex items-center gap-2 bg-brand-emerald text-white text-sm font-medium px-4 py-2 rounded-full hover:bg-brand-emeraldHover transition-colors">
                  <Navigation className="w-4 h-4" /> Open in Google Maps
                </a>
                <a href={rapidoLink()} target="_blank" rel="noopener noreferrer" data-testid="open-rapido" className="inline-flex items-center gap-2 border border-brand-line text-brand-ink text-sm font-medium px-4 py-2 rounded-full hover:bg-secondary transition-colors">
                  <Navigation className="w-4 h-4 text-brand-terracotta" /> Get Directions
                </a>
              </div>
            </div>
          </div>

          {/* Nearby */}
          {feature("nearby_properties") && (
          <div className="mt-8">
            <h3 className="font-heading font-semibold text-lg mb-4">{t("prop.nearby")}</h3>
            {nearbyLoading ? (
              <div className="flex items-center gap-2 text-brand-muted text-sm"><Loader2 className="w-4 h-4 animate-spin" /> {t("loading")}</div>
            ) : nearby ? (
              <div className="grid sm:grid-cols-2 gap-3">
                {Object.entries(nearby).filter(([, v]) => v && v.length).map(([key, list]) => {
                  const meta = nearbyMeta[key];
                  const Icon = meta?.icon || MapPin;
                  return (
                    <div key={key} className="bg-card border border-brand-line rounded-xl p-4">
                      <div className="flex items-center gap-2 text-brand-emerald font-medium text-sm mb-2"><Icon className="w-4 h-4" /> {meta?.label || key}</div>
                      {list.map((item, i) => (
                        <div key={i} className="flex justify-between text-sm text-brand-ink/80 py-0.5">
                          <span className="line-clamp-1 pr-2">{item.name}</span>
                          <span className="text-brand-muted shrink-0">{item.distance_km} km</span>
                        </div>
                      ))}
                    </div>
                  );
                })}
              </div>
            ) : <p className="text-brand-muted text-sm">Nearby data not available.</p>}
          </div>
          )}

          {/* Video Walkthrough */}
          {feature("video_walkthrough") && prop.video_url && (
            <div className="mt-8" data-testid="video-walkthrough">
              <h3 className="font-heading font-semibold text-lg mb-3 flex items-center gap-2"><PlayCircle className="w-5 h-5 text-brand-emerald" /> Video Walkthrough</h3>
              <div className="rounded-2xl overflow-hidden border border-brand-line aspect-video bg-black">
                <iframe title="video" className="w-full h-full" src={embedUrl(prop.video_url)} allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen />
              </div>
            </div>
          )}

          {/* Price History */}
          {feature("price_history") && prop.price_history && prop.price_history.length > 0 && (
            <div className="mt-8" data-testid="price-history">
              <h3 className="font-heading font-semibold text-lg mb-3 flex items-center gap-2"><TrendingUp className="w-5 h-5 text-brand-emerald" /> Price History</h3>
              <div className="bg-card border border-brand-line rounded-2xl divide-y divide-brand-line">
                {[...prop.price_history].reverse().map((h, i) => (
                  <div key={i} className="flex items-center justify-between px-4 py-3" data-testid={`price-history-row-${i}`}>
                    <span className="text-sm text-brand-muted">{h.date ? new Date(h.date).toLocaleDateString() : "—"}</span>
                    <span className="font-heading font-semibold text-brand-ink">{formatPrice(h.price)}</span>
                  </div>
                ))}
              </div>
              {prop.price_history.length === 1 && <p className="text-xs text-brand-muted mt-2">No price changes yet — this is the original listed price.</p>}
            </div>
          )}

          {/* EMI Calculator */}
          {feature("emi_calculator") && (
            <div className="mt-8">
              <EmiCalculator defaultAmount={Math.round((prop.price || 0) * 0.8)} />
            </div>
          )}
        </div>

        {/* Sidebar */}
        <div className="order-2 lg:order-none lg:col-start-2 lg:row-start-1 lg:row-span-2 lg:sticky lg:top-20 h-fit space-y-4">
          <div className="bg-card border border-brand-line rounded-2xl p-6">
            <span className="inline-block bg-brand-emerald/10 text-brand-emerald text-xs font-semibold px-3 py-1 rounded-full mb-3">{typeLabel}</span>
            <h1 className="font-heading font-bold text-2xl text-brand-ink tracking-tight">{prop.title}</h1>
            <p className="flex items-center gap-1.5 text-brand-muted mt-2 text-sm">
              <MapPin className="w-4 h-4" /> {prop.area_name}{prop.city ? `, ${prop.city}` : ""} {prop.pincode && `- ${prop.pincode}`}
            </p>
            <div className="flex items-center gap-3 mt-3 flex-wrap">
              {feature("views_counter") && (
                <span className="flex items-center gap-1 text-xs text-brand-muted" data-testid="detail-views"><Eye className="w-4 h-4" /> {prop.views || 0} views</span>
              )}
              {prop.short_code && feature("property_id_display") && (
                <button onClick={copyId} data-testid="detail-copy-id" title="Copy Property ID & link"
                  className="flex items-center gap-1 text-xs font-semibold text-brand-muted bg-secondary rounded-full px-2.5 py-1 hover:text-brand-emerald hover:bg-brand-emerald/10 transition-colors">
                  <Copy className="w-3.5 h-3.5" /> ID: {prop.short_code}
                </button>
              )}
              {feature("whatsapp_share") && (
                <ShareMenu title={prop.title} price={formatPrice(prop.price, prop.price_unit)} testid="detail-share"
                  trigger={<button className="flex items-center gap-1 text-xs font-medium text-brand-emerald hover:underline"><Share2 className="w-4 h-4" /> Share</button>} />
              )}
              {feature("report_listing") && (
                <button onClick={() => setReportOpen(true)} data-testid="detail-report" className="flex items-center gap-1 text-xs font-medium text-brand-terracotta hover:underline"><Flag className="w-4 h-4" /> Report</button>
              )}
            </div>
            <div className="font-heading font-extrabold text-3xl text-brand-emerald mt-4">{formatPrice(prop.price, prop.price_unit)}</div>

            <div className="mt-4"><WhatsAppContact property={prop} variant="detail" /></div>

            {prop.broker && (
              <div className="mt-5 pt-5 border-t border-brand-line">
                <div className="text-xs uppercase tracking-widest text-brand-muted mb-2">{prop.lister_role === "owner" ? "Listed by (Owner)" : t("prop.badge")}</div>
                <div className="flex items-center gap-3">
                  {prop.broker.picture ? (
                    <img src={prop.broker.picture.startsWith("http") ? prop.broker.picture : `${process.env.REACT_APP_BACKEND_URL}${prop.broker.picture}`} alt={prop.broker.name} className="w-12 h-12 rounded-full object-cover border-2 border-brand-emerald/30 shrink-0" data-testid="broker-photo" />
                  ) : (
                    <div className="w-12 h-12 rounded-full bg-brand-emerald/10 flex items-center justify-center border-2 border-brand-emerald/20 shrink-0"><UserRound className="w-6 h-6 text-brand-emerald" /></div>
                  )}
                  <div>
                    <div className="font-semibold text-brand-ink">{prop.broker.name}</div>
                    {prop.lister_role === "owner" ? <div className="text-sm text-brand-muted">Individual Owner</div> : prop.broker.badge_number && <div className="text-sm text-brand-emerald font-medium">{prop.broker.badge_number}</div>}
                  </div>
                </div>
                {/* Broker rating */}
                <div className="mt-3 flex items-center gap-2" data-testid="broker-rating">
                  <StarRating value={rating.avg} />
                  <span className="text-sm text-brand-muted">{rating.count ? `${rating.avg} (${rating.count})` : "No ratings yet"}</span>
                </div>
                {user && user.role === "customer" && (
                  <div className="mt-3 bg-secondary rounded-xl p-3" data-testid="rate-broker">
                    <div className="text-xs font-medium text-brand-ink mb-1.5">{rating.my_rating ? "Update your rating" : "Rate this " + (prop.lister_role === "owner" ? "owner" : "mediator")}</div>
                    <StarInput value={myStars} onChange={setMyStars} />
                    <Textarea value={myReview} onChange={(e) => setMyReview(e.target.value)} placeholder="Write a short review (optional)" className="mt-2 text-sm bg-card" data-testid="review-text" />
                    <Button size="sm" onClick={submitRating} className="mt-2 rounded-full bg-brand-emerald" data-testid="submit-rating"><Star className="w-4 h-4 mr-1" />{rating.my_rating ? "Update Rating" : "Submit Rating"}</Button>
                  </div>
                )}
                {rating.reviews && rating.reviews.length > 0 && (
                  <div className="mt-3 space-y-2 max-h-40 overflow-y-auto no-scrollbar">
                    {rating.reviews.filter((rv) => rv.review).slice(0, 5).map((rv, i) => (
                      <div key={i} className="text-xs bg-card border border-brand-line rounded-lg p-2" data-testid={`review-${i}`}>
                        <div className="flex items-center gap-1.5"><StarRating value={rv.stars} size="w-3 h-3" /><span className="font-medium text-brand-ink">{rv.customer_name}</span></div>
                        <p className="text-brand-muted mt-1">{rv.review}</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Enquiry / status */}
            <div className="mt-6">
              {!user ? (
                <Button className="w-full rounded-full bg-brand-terracotta hover:bg-brand-terracottaHover" onClick={() => toast.info("Please login as a customer to enquire")} data-testid="login-to-enquire">{t("prop.enquire")}</Button>
              ) : user.role === "customer" ? (
                myEnquiry ? (
                  feature("enquiry_tracker") ? (
                  <div className="space-y-3" data-testid="enquiry-status">
                    <div className={`rounded-xl p-4 text-sm ${confirmed ? "bg-brand-sale/10 text-brand-sale" : myEnquiry.status === "rejected" ? "bg-brand-terracotta/10 text-brand-terracotta" : myEnquiry.status === "responded" ? "bg-brand-emerald/10 text-brand-emerald" : "bg-brand-lease/10 text-brand-lease"}`}>
                      {confirmed ? t("enquiry.confirmed") : myEnquiry.status === "rejected" ? t("enquiry.rejected") : myEnquiry.status === "responded" ? t("enq.alert") : t("enquiry.pending")}
                    </div>
                    {myEnquiry.due_date && !myEnquiry.broker_response && (
                      <div className="flex items-center gap-2 text-xs text-brand-muted"><CalendarClock className="w-4 h-4" /> {t("enq.due")}: {new Date(myEnquiry.due_date).toLocaleDateString()}</div>
                    )}
                    {myEnquiry.broker_response && (
                      <div className="bg-card border border-brand-line rounded-xl p-4">
                        <div className="text-xs uppercase tracking-widest text-brand-muted mb-1">{t("enq.response")}</div>
                        <p className="text-brand-ink text-sm">{myEnquiry.broker_response}</p>
                        {myEnquiry.response_date && <div className="text-xs text-brand-emerald mt-2 flex items-center gap-1"><CalendarClock className="w-3.5 h-3.5" /> {myEnquiry.response_date}</div>}
                      </div>
                    )}
                  </div>
                  ) : (
                  <div className={`rounded-xl p-4 text-sm ${confirmed ? "bg-brand-sale/10 text-brand-sale" : "bg-brand-lease/10 text-brand-lease"}`} data-testid="enquiry-status-min">
                    {confirmed ? t("enquiry.confirmed") : "Enquiry submitted. Our team will contact you."}
                  </div>
                  )
                ) : (
                  <Button className="w-full rounded-full bg-brand-terracotta hover:bg-brand-terracottaHover" onClick={openEnquiry} data-testid="enquire-btn">{t("prop.enquire")}</Button>
                )
              ) : null}
              <p className="text-xs text-brand-muted mt-3 text-center">{t("prop.enquiryNote")}</p>
            </div>
          </div>

          <SidebarLoanOptions property={prop} />

          {/* Payment */}
          {confirmed && settings && (
            <div className="bg-brand-emerald text-white rounded-2xl p-6 grain relative overflow-hidden" data-testid="payment-card">
              <div className="relative z-10">
                <h3 className="font-heading font-semibold text-lg flex items-center gap-2"><QrCode className="w-5 h-5" /> {t("pay.title")}</h3>
                {settings.upi_qr ? (
                  <img src={fileUrl(settings.upi_qr)} alt="UPI QR" className="w-44 h-44 bg-white rounded-xl p-2 mx-auto mt-4" />
                ) : (
                  <div className="w-44 h-44 bg-white/10 rounded-xl mx-auto mt-4 flex items-center justify-center text-white/60 text-sm text-center px-3">QR will be shared by admin</div>
                )}
                <p className="text-center text-white/70 text-xs mt-3">{t("pay.scan")}</p>
                <div className="mt-3 bg-white/10 rounded-xl p-3 text-center">
                  <div className="text-xs text-white/60">{t("pay.upi")}</div>
                  <div className="font-heading font-semibold">{settings.upi_id || "—"}</div>
                </div>
                {checkoutOffers.length > 0 && (
                  <div className="mt-4 bg-white/10 rounded-xl p-3" data-testid="checkout-coupons">
                    <div className="text-xs text-white/70 mb-2 flex items-center gap-1"><BadgePercent className="w-4 h-4" /> Apply a coupon</div>
                    {checkoutOffers.map((o) => (
                      <div key={o.id} className="flex items-center justify-between gap-2 py-1.5">
                        <div className="text-sm">
                          <div className="font-medium leading-tight">{o.title}</div>
                          {o.discount && <div className="text-[11px] text-white/70">{o.discount}</div>}
                        </div>
                        {couponCodes[o.id] ? (
                          <span className="text-xs font-bold bg-white text-brand-emerald px-2.5 py-1 rounded tracking-widest" data-testid={`checkout-code-${o.id}`}>{couponCodes[o.id]}</span>
                        ) : (
                          <Button size="sm" className="rounded-full bg-white text-brand-emerald hover:bg-white/90 h-7" onClick={() => validateCoupon(o.id)} data-testid={`checkout-validate-${o.id}`}><Ticket className="w-3.5 h-3.5 mr-1" />Apply</Button>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      <Dialog open={enqOpen} onOpenChange={setEnqOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>{t("prop.enquire")}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="flex items-center gap-1.5"><Phone className="w-4 h-4" /> {t("auth.phone")}</Label>
              <div className="flex gap-2 mt-1.5">
                <select value={enqDial} onChange={(e) => setEnqDial(e.target.value)} data-testid="enq-country-code" className="w-24 shrink-0 rounded-xl border border-brand-line bg-background px-2 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-emerald/40">
                  {["+91", "+1", "+44", "+971", "+61", "+65", "+966"].map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
                <Input value={enqPhone} onChange={(e) => setEnqPhone(e.target.value)} className="flex-1" data-testid="enq-phone" placeholder="Mobile number" />
              </div>
            </div>
            <div>
              <Label>{t("form.desc")}</Label>
              <Textarea value={enqMsg} onChange={(e) => setEnqMsg(e.target.value)} placeholder="I'm interested in this property..." className="mt-1.5" data-testid="enq-message" />
            </div>
            <p className="text-xs text-brand-muted">{t("prop.enquiryNote")}</p>
            <Button onClick={submitEnquiry} disabled={submitting} className="w-full rounded-full bg-brand-emerald" data-testid="enq-submit">
              {submitting ? t("loading") : t("prop.enquire")}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={reportOpen} onOpenChange={setReportOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Flag className="w-4 h-4 text-brand-terracotta" /> Report this listing</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-brand-muted">Tell us what's wrong (fraud, wrong info, already sold, etc.). Our team will review it.</p>
            <Textarea value={reportReason} onChange={(e) => setReportReason(e.target.value)} placeholder="Describe the issue..." data-testid="report-reason" />
            <Button onClick={submitReport} className="w-full rounded-full bg-brand-terracotta" data-testid="report-submit"><Flag className="w-4 h-4 mr-1.5" /> Submit Report</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Lightbox */}
      {lightbox >= 0 && photos.length > 0 && (
        <div className="fixed inset-0 z-[120] bg-black/90 flex items-center justify-center" onClick={() => setLightbox(-1)} data-testid="lightbox">
          <button className="absolute top-5 right-5 text-white/80 hover:text-white" onClick={() => setLightbox(-1)} data-testid="lightbox-close"><X className="w-8 h-8" /></button>
          {photos.length > 1 && (
            <button className="absolute left-4 sm:left-8 text-white/80 hover:text-white" onClick={(e) => { e.stopPropagation(); setLightbox((lightbox - 1 + photos.length) % photos.length); }} data-testid="lightbox-prev"><ChevronLeft className="w-10 h-10" /></button>
          )}
          <img src={photos[lightbox]} alt="" className="max-h-[85vh] max-w-[90vw] object-contain rounded-lg" onClick={(e) => e.stopPropagation()} />
          {photos.length > 1 && (
            <button className="absolute right-4 sm:right-8 text-white/80 hover:text-white" onClick={(e) => { e.stopPropagation(); setLightbox((lightbox + 1) % photos.length); }} data-testid="lightbox-next"><ChevronRight className="w-10 h-10" /></button>
          )}
          <span className="absolute bottom-5 text-white/70 text-sm">{lightbox + 1} / {photos.length}</span>
        </div>
      )}

      <MobileStickyCTA property={prop} />
    </div>
  );
}

function DetailStat({ icon: Icon, label, value }) {
  return (
    <div className="bg-card border border-brand-line rounded-xl p-4">
      <Icon className="w-5 h-5 text-brand-emerald mb-2" />
      <div className="text-xs text-brand-muted">{label}</div>
      <div className="font-semibold text-brand-ink capitalize">{value}</div>
    </div>
  );
}
